// src/app/company/manage/map/services/draw-validation.service.ts
import { Injectable } from '@angular/core';
import * as mapboxgl from 'mapbox-gl';
import MapboxDraw from '@mapbox/mapbox-gl-draw';
import * as turf from '@turf/turf';
import dissolve from '@turf/dissolve';
import polygonize from '@turf/polygonize';

import type {
  Feature,
  FeatureCollection,
  GeoJsonProperties,
  Geometry,
  Polygon as GJPolygon,
  MultiPolygon,
  LineString,
  MultiLineString,
} from 'geojson';

/* ------------ ids & constants ------------ */
const SRC_KINKS = 'val-kinks-src';
const LYR_KINKS = 'val-kinks-layer';
const INTERSECTION_AREA_EPS = 1e-8;
const KINK_ZOOM = 16;

/* ------------ utils (tiny) ------------ */
const isNum = (v: unknown): v is number => typeof v === 'number' && Number.isFinite(v);
const emptyFC = (): FeatureCollection => ({ type: 'FeatureCollection', features: [] });

function ensureClosed(ring: [number, number][] | null | undefined): [number, number][] {
  if (!Array.isArray(ring) || ring.length === 0) return [];
  const clean: [number, number][] = ring.filter(
    (p): p is [number, number] => Array.isArray(p) && isNum(p[0]) && isNum(p[1])
  );
  if (clean.length === 0) return [];
  const [fx, fy] = clean[0];
  const [lx, ly] = clean[clean.length - 1];
  return fx === lx && fy === ly ? clean : [...clean, [fx, fy]];
}
function signedArea(ring: [number, number][]): number {
  const r = ensureClosed(ring);
  if (r.length < 3) return 0;
  let sum = 0;
  for (let i = 0; i < r.length - 1; i++) {
    const [x1, y1] = r[i],
      [x2, y2] = r[i + 1];
    sum += (x2 - x1) * (y2 + y1);
  }
  return -sum;
}
function isClockwise(ring: [number, number][] | null | undefined): boolean {
  const r = ensureClosed(ring ?? []);
  if (r.length < 3) return true;
  return signedArea(r) < 0;
}
function enforceLeftHandRings(coords: [number, number][][] | null | undefined): [number, number][][] {
  if (!Array.isArray(coords) || coords.length === 0) return [];
  const out: [number, number][][] = [];
  const outer = ensureClosed(coords[0]);
  if (outer.length >= 4) out.push(isClockwise(outer) ? outer : [...outer].reverse());
  for (let i = 1; i < coords.length; i++) {
    const hole = ensureClosed(coords[i]);
    if (hole.length >= 4) out.push(!isClockwise(hole) ? hole : [...hole].reverse());
  }
  return out;
}
function orientedFeatureKeepId(f: Feature<GJPolygon>): Feature<GJPolygon> {
  const fixed = enforceLeftHandRings((f.geometry?.coordinates ?? []) as [number, number][][]);
  const out: Feature<GJPolygon> = {
    type: 'Feature',
    properties: { ...(f.properties ?? {}) },
    geometry: { type: 'Polygon', coordinates: fixed },
  };
  (out as any).id = (f as any).id;
  return out;
}
function bboxLike(bb: [number, number, number, number]): mapboxgl.LngLatBoundsLike {
  return [
    [bb[0], bb[1]],
    [bb[2], bb[3]],
  ];
}
function splitToPolygons(
  f: Feature<Geometry, GeoJsonProperties> | null,
  props: Record<string, any> = {}
): Feature<GJPolygon>[] {
  if (!f || !f.geometry) return [];
  const g = f.geometry;
  if (g.type === 'Polygon') {
    const c = enforceLeftHandRings(g.coordinates as any);
    return c.length
      ? [
          {
            type: 'Feature',
            properties: { ...(f.properties ?? {}), ...props },
            geometry: { type: 'Polygon', coordinates: c },
          },
        ]
      : [];
  }
  if (g.type === 'MultiPolygon') {
    const out: Feature<GJPolygon>[] = [];
    for (const part of g.coordinates as [number, number][][][]) {
      const c = enforceLeftHandRings(part);
      if (c.length)
        out.push({
          type: 'Feature',
          properties: { ...(f.properties ?? {}), ...props },
          geometry: { type: 'Polygon', coordinates: c },
        });
    }
    return out;
  }
  if (g.type === 'GeometryCollection') {
    const out: Feature<GJPolygon>[] = [];
    for (const piece of g.geometries ?? [])
      out.push(...splitToPolygons({ type: 'Feature', properties: f.properties ?? {}, geometry: piece }, props));
    return out;
  }
  return [];
}

/* ------------ issues model ------------ */
type IssueKind = 'overlap' | 'kinks' | 'orientation';
type Issue =
  | { id: string; kind: 'kinks'; message: string; center: mapboxgl.LngLatLike }
  | { id: string; kind: 'orientation'; message: string; bbox: mapboxgl.LngLatBoundsLike }
  | { id: string; kind: 'overlap'; message: string; ids: [string, string]; bbox: mapboxgl.LngLatBoundsLike };

/* ------------ Error list control (bottom-right) ------------ */
class ErrorListControl implements mapboxgl.IControl {
  private _map: mapboxgl.Map | null = null;
  private _container!: HTMLElement;
  private list!: HTMLElement;
  private errs: Issue[] = [];
  private selectedId: string | null = null;

  constructor(
    private onRowClick: (it: Issue) => void,
    private onFixOverlap: (issue: Extract<Issue, { kind: 'overlap' }>) => void
  ) {}

  onAdd(map: mapboxgl.Map) {
    this._map = map;
    const root = document.createElement('div');
    root.className = 'mapboxgl-ctrl mapboxgl-ctrl-group validation-ctrl';
    root.style.display = 'none';
    root.style.maxWidth = '360px';
    root.style.minWidth = '220px';
    root.style.flexDirection = 'column';

    const header = document.createElement('div');
    header.textContent = 'Issues';
    header.style.fontSize = '12px';
    header.style.fontWeight = '600';
    header.style.padding = '6px 8px';
    header.style.borderBottom = '1px solid #eee';

    const wrap = document.createElement('div');
    wrap.style.maxHeight = '280px';
    wrap.style.overflow = 'auto';

    const list = document.createElement('div');
    list.style.display = 'flex';
    list.style.flexDirection = 'column';
    list.style.gap = '6px';
    list.style.padding = '6px';

    wrap.appendChild(list);
    root.appendChild(header);
    root.appendChild(wrap);

    this._container = root;
    this.list = list;
    return this._container;
  }
  onRemove() {
    this._container?.parentNode?.removeChild(this._container);
    this._map = null;
  }

  setErrors(errs: Issue[]) {
    const ids = new Set(errs.map((e) => e.id));
    if (this.selectedId && !ids.has(this.selectedId)) this.selectedId = null;
    this.errs = errs.slice();
    this.render();
  }

  private render() {
    if (!this._container) return;
    const has = this.errs.length > 0;
    this._container.style.display = has ? 'flex' : 'none';

    this.list.replaceChildren();
    for (const e of this.errs) {
      const row = document.createElement('div');
      row.style.display = 'grid';
      row.style.gridTemplateColumns = e.kind === 'overlap' ? '1fr auto' : '1fr';
      row.style.alignItems = 'center';
      row.style.gap = '8px';
      row.style.border = '1px solid #eee';
      row.style.borderRadius = '6px';
      row.style.padding = '6px 8px';
      row.style.background = this.selectedId === e.id ? '#e8f0fe' : '#fff';
      row.style.cursor = 'pointer';
      row.style.transition = 'background 120ms ease';

      row.addEventListener('mouseenter', () => {
        if (this.selectedId !== e.id) row.style.background = '#f7f7f7';
      });
      row.addEventListener('mouseleave', () => {
        row.style.background = this.selectedId === e.id ? '#e8f0fe' : '#fff';
      });

      const label = document.createElement('div');
      label.style.fontSize = '12px';
      label.textContent = e.message;
      row.appendChild(label);

      if (e.kind === 'overlap') {
        const fix = document.createElement('button');
        fix.type = 'button';
        fix.textContent = 'Fix';
        fix.style.padding = '4px 8px';
        fix.style.fontSize = '11px';
        fix.addEventListener('click', (ev) => {
          ev.stopPropagation();
          this.onFixOverlap(e);
        });
        row.appendChild(fix);
      }

      row.addEventListener('click', () => {
        this.selectedId = e.id;
        this.render(); // highlight clicked
        this.onRowClick(e);
      });

      this.list.appendChild(row);
    }
  }
}

/* ------------ Service (worker + throttling) ------------ */
@Injectable({ providedIn: 'root' })
export class DrawValidationService {
  private map: mapboxgl.Map | null = null;
  private draw: MapboxDraw | null = null;
  private ctrl: ErrorListControl | null = null;
  private onAfterFix?: () => void;

  // worker & scheduling
  private worker: Worker | null = null;
  private pending = false;
  private needsRerun = false;
  private lastRun = 0;
  private throttleMs = 220; // smoother during vertex drag

  attach(map: mapboxgl.Map, draw: MapboxDraw, options?: { onAfterFix?: () => void }) {
    this.map = map;
    this.draw = draw;
    this.onAfterFix = options?.onAfterFix;

    this.ensureKinkLayer();

    // bottom-right issues panel
    this.ctrl = new ErrorListControl(
      (it) => this.zoomToIssue(it),
      (ov) => this.fixSingleOverlap(ov)
    );
    try {
      this.map.addControl(this.ctrl as any, 'bottom-right');
    } catch {}

    // spin up worker once
    this.initWorker();

    // Only listen to *meaningful* events, and throttle internal recompute
    const schedule = () => this.recompute();
    map.on('draw.create', schedule);
    map.on('draw.delete', schedule);
    map.on('draw.combine', schedule);
    map.on('draw.uncombine', schedule);
    map.on('draw.selectionchange', schedule);
    map.on('draw.modechange', schedule);
    // draw.update fires a lot while dragging → rely on throttle
    map.on('draw.update', schedule);

    // after programmatic adds (uploads), you can call recompute() explicitly from your component too.
    Promise.resolve().then(() => this.computeAndRender());
  }

  detach() {
    if (!this.map) return;
    try {
      if (this.ctrl) (this.map as any).removeControl?.(this.ctrl);
    } catch {}
    try {
      if (this.map.getLayer(LYR_KINKS)) this.map.removeLayer(LYR_KINKS);
      if (this.map.getSource(SRC_KINKS)) this.map.removeSource(SRC_KINKS);
    } catch {}
    if (this.worker) {
      this.worker.terminate();
      this.worker = null;
    }
    this.ctrl = null;
    this.map = null;
    this.draw = null;
    this.onAfterFix = undefined;
  }

  /** public: let your component call after large uploads if needed */
  public recompute() {
    const now = Date.now();
    if (this.pending || now - this.lastRun < this.throttleMs) {
      this.needsRerun = true;
      return;
    }
    this.lastRun = now;
    this.computeAndRender();
  }

  /* ---------- worker pipeline ---------- */
  private initWorker() {
    try {
      this.worker = new Worker(new URL('./draw-validation.worker', import.meta.url), { type: 'module' });
      this.worker.onmessage = (ev: MessageEvent) => {
        const { type, payload } = ev.data || {};
        if (type !== 'result') return;
        this.pending = false;

        // draw kinks
        try {
          const src = this.map?.getSource(SRC_KINKS) as mapboxgl.GeoJSONSource | undefined;
          src?.setData(payload.kinkPoints);
        } catch {}

        // map worker issues back to map types
        const issues: Issue[] = (payload.issues as any[]).map((e) => {
          if (e.kind === 'kinks') return { ...e, center: e.center as mapboxgl.LngLatLike };
          if (e.kind === 'orientation') return { ...e, bbox: bboxLike(e.bbox) };
          if (e.kind === 'overlap') return { ...e, bbox: bboxLike(e.bbox) };
          return e;
        });

        this.ctrl?.setErrors(issues);

        // handle queued rerun (coalesced while we were busy)
        if (this.needsRerun) {
          this.needsRerun = false;
          this.recompute();
        }
      };
    } catch {
      this.worker = null; // fallback to main thread if worker creation fails
    }
  }

  private computeAndRender() {
    if (!this.map || !this.draw) return;

    const polys = this.getAllPolygonsWithIds();
    if (this.worker) {
      this.pending = true;
      this.worker.postMessage({ type: 'compute', payload: { polys } });
      return;
    }

    // Fallback: if worker not available, you can optionally no-op or run a minimal check
    // To keep main thread light, we'll only show orientation issues in fallback.
    const issues: Issue[] = [];
    for (let i = 0; i < polys.length; i++) {
      const coords = (polys[i].feat.geometry.coordinates ?? []) as [number, number][][];
      if (coords.length) {
        if (!isClockwise(coords[0])) {
          const bb = turf.bbox(polys[i].feat as any) as [number, number, number, number];
          issues.push({
            id: `orient-${i}-outer`,
            kind: 'orientation',
            message: `Polygon ${i + 1} outer ring is not clockwise (left-hand rule).`,
            bbox: bboxLike(bb),
          });
        }
        for (let r = 1; r < coords.length; r++) {
          if (isClockwise(coords[r])) {
            const bb = turf.bbox(polys[i].feat as any) as [number, number, number, number];
            issues.push({
              id: `orient-${i}-hole-${r}`,
              kind: 'orientation',
              message: `Polygon ${i + 1} hole #${r} is not counter-clockwise (left-hand rule).`,
              bbox: bboxLike(bb),
            });
          }
        }
      }
    }
    try {
      const src = this.map.getSource(SRC_KINKS) as mapboxgl.GeoJSONSource | undefined;
      src?.setData(emptyFC());
    } catch {}
    this.ctrl?.setErrors(issues);
  }

  /* ---------- navigation & fix ---------- */
  private zoomToIssue(it: Issue) {
    if (!this.map) return;
    try {
      if (it.kind === 'kinks') this.map.flyTo({ center: it.center, zoom: KINK_ZOOM });
      else this.map.fitBounds((it as any).bbox, { padding: 40 });
    } catch {}
  }

  private fixSingleOverlap(issue: Extract<Issue, { kind: 'overlap' }>) {
    if (!this.draw) return;

    // gather cluster connected by overlaps (so we dissolve both partial & chain overlaps)
    const items = this.getAllPolygonsWithIds();
    const idxById = new Map<string, number>();
    for (let i = 0; i < items.length; i++) idxById.set(items[i].id, i);

    const ia = idxById.get(issue.ids[0]);
    const ib = idxById.get(issue.ids[1]);
    if (ia == null || ib == null) {
      this.recompute();
      return;
    }

    const n = items.length;
    const adj: number[][] = Array.from({ length: n }, () => []);
    for (let i = 0; i < n; i++)
      for (let j = i + 1; j < n; j++) {
        const area = this.fastOverlap(items[i].feat as any, items[j].feat as any);
        if (area > INTERSECTION_AREA_EPS) {
          adj[i].push(j);
          adj[j].push(i);
        }
      }

    const visit = (s: number) => {
      const seen = new Set<number>([s]);
      const q = [s];
      while (q.length) {
        const v = q.shift()!;
        for (const w of adj[v])
          if (!seen.has(w)) {
            seen.add(w);
            q.push(w);
          }
      }
      return seen;
    };
    const comp = new Set<number>([...visit(ia), ...visit(ib)]);
    if (comp.size <= 1) {
      this.recompute();
      return;
    }

    const cluster = [...comp].map((k) => items[k].feat);
    const dissolved = this.dissolveCluster(cluster);
    if (!dissolved?.geometry) {
      this.recompute();
      return;
    }

    // Replace originals with dissolved pieces
    const delIds = [...comp].map((k) => items[k].id);
    try {
      (this.draw as any).delete(delIds);
    } catch {}

    const out = splitToPolygons(dissolved, { shape: 'polygon', _circle: false });
    try {
      if (out.length === 1) (this.draw as any).add(out[0] as any);
      else if (out.length > 1) (this.draw as any).add({ type: 'FeatureCollection', features: out } as any);
    } catch {}

    // refresh polygon list in your component if you passed a callback
    this.onAfterFix?.();

    // queue a recompute after the Draw events settle
    setTimeout(() => this.recompute(), 0);
  }

  private fastOverlap(a: turf.AllGeoJSON, b: turf.AllGeoJSON): number {
    // very fast bbox pre-filter before calling worker-grade overlap
    try {
      const ba = turf.bbox(a as any) as [number, number, number, number];
      const bb = turf.bbox(b as any) as [number, number, number, number];
      if (!(ba?.every(isNum) && bb?.every(isNum))) return 0;
      if (ba[2] < bb[0] || bb[2] < ba[0] || ba[3] < bb[1] || bb[3] < ba[1]) return 0;
      // quick intersect (still on main thread, but rare because of bbox filter)
      const inter = turf.intersect(a as any, b as any) as any;
      if (inter?.geometry) {
        const area = turf.area(inter as any);
        return isNum(area) ? Math.max(0, area) : 0;
      }
    } catch {}
    return 0;
  }

  private dissolveCluster(features: Feature<GJPolygon>[]): Feature<Geometry> | null {
    if (!features.length) return null;
    const fc: FeatureCollection = { type: 'FeatureCollection', features: features as any };

    // 1) dissolve (returns FC) → buffer(0)
    try {
      const dFC = dissolve(fc as any) as FeatureCollection<GJPolygon | MultiPolygon>;
      if (dFC?.features?.length) {
        let merged: Feature<Geometry> | null = null;
        if (dFC.features.length === 1) merged = dFC.features[0] as unknown as Feature<Geometry>;
        else {
          const parts: [number, number][][][] = [];
          for (const f of dFC.features) {
            if (f.geometry.type === 'Polygon') parts.push(f.geometry.coordinates as any);
            else if (f.geometry.type === 'MultiPolygon') for (const p of f.geometry.coordinates as any) parts.push(p);
          }
          merged =
            parts.length === 1
              ? { type: 'Feature', properties: {}, geometry: { type: 'Polygon', coordinates: parts[0] } }
              : ({
                  type: 'Feature',
                  properties: {},
                  geometry: { type: 'MultiPolygon', coordinates: parts },
                } as Feature<Geometry>);
        }
        try {
          const b0 = turf.buffer(merged as any, 0) as any as Feature<Geometry>;
          if (b0?.geometry) return b0;
        } catch {}
        return merged;
      }
    } catch {}

    // 2) polygonize fallback
    try {
      const lines = turf.polygonToLine(fc as any) as FeatureCollection<LineString | MultiLineString>;
      const polyFC = polygonize(lines as any) as FeatureCollection<GJPolygon>;
      if (polyFC?.features?.length) {
        let merged: Feature<Geometry> | null;
        if (polyFC.features.length === 1) merged = polyFC.features[0] as unknown as Feature<Geometry>;
        else {
          const parts: [number, number][][][] = [];
          for (const f of polyFC.features) parts.push(f.geometry.coordinates as any);
          merged = {
            type: 'Feature',
            properties: {},
            geometry: { type: 'MultiPolygon', coordinates: parts },
          } as Feature<Geometry>;
        }
        try {
          const b0 = turf.buffer(merged as any, 0) as any as Feature<Geometry>;
          if (b0?.geometry) return b0;
        } catch {}
        return merged;
      }
    } catch {}

    // 3) give up
    return null;
  }

  /* ---------- helpers ---------- */
  private ensureKinkLayer() {
    if (!this.map) return;
    try {
      if (!this.map.getSource(SRC_KINKS)) {
        this.map.addSource(SRC_KINKS, { type: 'geojson', data: emptyFC() } as any);
      }
      if (!this.map.getLayer(LYR_KINKS)) {
        this.map.addLayer({
          id: LYR_KINKS,
          type: 'circle',
          source: SRC_KINKS,
          paint: {
            'circle-radius': 6,
            'circle-color': '#ff1744',
            'circle-stroke-width': 1.5,
            'circle-stroke-color': '#fff',
          },
        } as any);
      }
    } catch {}
  }

  private getAllPolygonsWithIds(): Array<{ id: string; feat: Feature<GJPolygon> }> {
    const out: Array<{ id: string; feat: Feature<GJPolygon> }> = [];
    if (!this.draw) return out;
    const fc = (this.draw.getAll() as FeatureCollection<Geometry, GeoJsonProperties>) ?? emptyFC();
    for (const f of fc.features || []) {
      if (!f?.geometry || f.geometry.type !== 'Polygon') continue;
      const id = String((f as any).id ?? '');
      if (!id) continue;
      out.push({ id, feat: orientedFeatureKeepId(f as Feature<GJPolygon>) });
    }
    return out;
  }
}
